import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CreateEasypassRequest, ParkingTypeService, ParkingSpaceDto } from '../../../../../api/gen';

export interface EasypassTypeResult {
    success: boolean;
    data?: any;
}

@Component({
    selector: 'app-add-easypass-type',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatDatepickerModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule
    ],
    providers: [provideNativeDateAdapter()],
    templateUrl: './add-easypass-type.component.html',
    styleUrl: './add-easypass-type.component.scss'
})
export class AddEasypassTypeComponent implements OnInit {
    private fb = inject(FormBuilder);
    private parkingTypeService = inject(ParkingTypeService);
    private snackBar = inject(MatSnackBar);

    @Input({ required: true }) parkingOperatorId: string | undefined;
    @Input() parkingSpace!: ParkingSpaceDto;
    @Output() submitted = new EventEmitter<EasypassTypeResult>();

    form!: FormGroup;
    isLoading = signal(false);

    ngOnInit(): void {
        this.initForm();
    }

    private initForm(): void {
        this.form = this.fb.group({
            selectedZones: [[]]
        });
    }

    availableZones() {
        return this.parkingSpace?.parkingZones || [];
    }

    /**
     * Creates a minimal Easypass invitation for this space (its own per-event ParkingType + token).
     * Richer invitations (capacity, title, cost center, tariff) are created via the
     * IssueEasypassDialogComponent — the "enable/availability" step no longer exists.
     */
    submit(): void {
        this.isLoading.set(true);

        const formValue = this.form.value;
        const selectedZones: string[] = formValue.selectedZones?.length > 0
            ? formValue.selectedZones
            : (this.parkingSpace?.parkingZones?.map(z => z.id).filter(Boolean) as string[]) ?? [];

        const request: CreateEasypassRequest = {
            operatorId: this.parkingOperatorId ?? null,
            availableForParkingSpaces: this.parkingSpace?.id ? [this.parkingSpace.id] : [],
            availableForParkingZones: selectedZones,
            organizationId: null,
            maxRedemptions: 1,
            isUnlimited: false,
            allowedEntries: 0
        };

        this.parkingTypeService.parkingTypesEasypassesPost(request).subscribe({
            next: (result) => {
                this.snackBar.open('Easypass invitation created for this space', 'Close', { duration: 3000 });
                this.isLoading.set(false);
                this.submitted.emit({ success: true, data: result });
            },
            error: (error) => {
                console.error('Error creating Easypass invitation:', error);
                this.snackBar.open('Failed to create Easypass invitation', 'Close', { duration: 3000 });
                this.isLoading.set(false);
                this.submitted.emit({ success: false });
            }
        });
    }
}
